<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useSpeckleAuthStore, useSpeckleStore } from '@haskoning/vue-speckle'

import { useCalculationStore } from '@/stores/calculation'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectSpeckleStore } from '@/stores/projectSpeckle'
import NoDataBar from '@/components/calculation-index/NoDataBar.vue'
import BulkActionDialog from '@/components/dialog/BulkActionDialog.vue'
import { NAME } from '@/constants/calculationFieldConstants'
import { Calculation } from '@/types/calcualtion.type'

const SPECKLE_MODEL_ID = 'selected_speckle_model_id'

const calculationStore = useCalculationStore()
const speckleStore = useSpeckleStore({ autoSelectData: false })
const speckleAuthStore = useSpeckleAuthStore()
const authorizationStore = useAuthorizationStore()
const projectSpeckleStore = useProjectSpeckleStore()

const { items, loading } = storeToRefs(calculationStore)
// storeToRefs does not type-check against the vue-speckle stores, so read them through computeds
const allModels = computed(() => speckleStore.allModels)
const currentSelection = computed(() => speckleStore.currentSelection)
const serverUrl = computed(() => speckleAuthStore.serverUrl)

const search = ref<string | null>('')
const savingIds = ref<number[]>([])
const failedIds = ref<number[]>([])
const linkByNameDialog = ref(false)
const linkingByName = ref(false)

const HEADER = [
  { title: '#', key: 'order', width: '5%', align: 'center', sortable: false },
  { title: 'Name', key: NAME, width: '30%' },
  { title: 'Speckle model', key: SPECKLE_MODEL_ID, width: '45%', sortable: false },
  { title: '', key: 'actions', width: '5%', align: 'center', sortable: false }
] as const

const canEditCalculation = computed(() =>
  authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['change_calculation']
  })
)

const hasSpeckleProject = computed(() => !!currentSelection.value.project)
const modelsLoading = computed(() => speckleStore.isGetting === 'models')

const filteredItems = computed<Calculation[]>(() =>
  (items.value as Calculation[]).filter((item) =>
    item.name.toLowerCase().includes((search.value ?? '').toLowerCase())
  )
)

const linkedCount = computed(
  () => (items.value as Calculation[]).filter((item) => isLinkedToExistingModel(item)).length
)

function isSaving(calculation: Calculation) {
  return savingIds.value.includes(calculation.id)
}

function isLinkedToExistingModel(calculation: Calculation) {
  const modelId = calculation[SPECKLE_MODEL_ID]
  return !!modelId && allModels.value.some((model) => model.id === modelId)
}

function linkErrors(calculation: Calculation) {
  if (failedIds.value.includes(calculation.id)) {
    return ['Could not save the link, please try again']
  }
  const isMissing =
    hasSpeckleProject.value &&
    !modelsLoading.value &&
    !!calculation[SPECKLE_MODEL_ID] &&
    !isLinkedToExistingModel(calculation)
  return isMissing ? ['Linked model not found in the selected Speckle project'] : []
}

function speckleModelUrl(modelId: string | null) {
  return `${serverUrl.value}/projects/${currentSelection.value.project?.id}/models/${modelId}`
}

async function updateLink(calculation: Calculation, modelId: string | null) {
  const previousModelId = calculation[SPECKLE_MODEL_ID]
  if (previousModelId === modelId) return

  calculation[SPECKLE_MODEL_ID] = modelId
  failedIds.value = failedIds.value.filter((id) => id !== calculation.id)
  savingIds.value.push(calculation.id)
  try {
    const response = await calculationStore.update({
      id: calculation.id,
      [SPECKLE_MODEL_ID]: modelId
    })
    if (!response) {
      calculation[SPECKLE_MODEL_ID] = previousModelId
      failedIds.value.push(calculation.id)
    }
  } finally {
    savingIds.value = savingIds.value.filter((id) => id !== calculation.id)
  }
}

// Prefer an exact name match, fall back to a case-insensitive one
function findModelIdByName(name: string) {
  const exactMatch = allModels.value.find((model) => model.name === name)
  if (exactMatch) return exactMatch.id
  const normalizedName = name.trim().toLowerCase()
  return (
    allModels.value.find((model) => model.name.trim().toLowerCase() === normalizedName)?.id ?? null
  )
}

async function linkAllByName() {
  linkingByName.value = true
  try {
    await speckleStore.getSpeckleModels()
    await Promise.all(
      (items.value as Calculation[]).map((calculation) =>
        updateLink(calculation, findModelIdByName(calculation.name))
      )
    )
  } finally {
    linkingByName.value = false
    linkByNameDialog.value = false
  }
}

// Runs synchronously so the saved project id is read before the new selection is persisted
watch(
  () => currentSelection.value.project?.id,
  (projectId, previousProjectId) => {
    if (!projectId || !canEditCalculation.value) return
    // Restoring the saved project on page load is not a change of project
    if (!previousProjectId && projectId === projectSpeckleStore.currentItem?.project_id) return
    linkAllByName()
  },
  { flush: 'sync' }
)
</script>

<template>
  <v-card border="opacity-35 sm" variant="text" class="mt-4">
    <v-card-title>Calculation models</v-card-title>
    <v-card-subtitle>
      Link each calculation to the Speckle model it is sent to.
      <span v-if="hasSpeckleProject">
        {{ linkedCount }} of {{ items.length }} calculations linked.
      </span>
    </v-card-subtitle>

    <v-alert
      v-if="!hasSpeckleProject"
      type="info"
      variant="tonal"
      density="compact"
      class="mx-4 mt-4"
    >
      Choose a Speckle project above before linking calculations to models.
    </v-alert>
    <v-alert v-else type="info" variant="tonal" density="compact" class="mx-4 mt-4">
      <ul class="pl-4">
        <li>
          Changing the Speckle project relinks every calculation to the model with the same name.
          Calculations without a matching model are left unlinked.
        </li>
        <li>
          When a calculation without a linked model is opened, a model with the calculation's name
          is created and linked automatically. If a model with that name already exists, it is
          linked instead.
        </li>
      </ul>
    </v-alert>

    <v-row class="pa-4" align="center">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          color="primary"
          hide-details
          clearable
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="8" class="d-flex justify-end">
        <v-btn
          v-if="canEditCalculation"
          color="primary"
          class="text-none mx-2"
          variant="tonal"
          prepend-icon="mdi-link-variant"
          :disabled="!hasSpeckleProject || modelsLoading || !items.length"
          :loading="linkingByName"
          @click="linkByNameDialog = true"
        >
          Link by name
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          variant="tonal"
          prepend-icon="mdi-refresh"
          :disabled="!hasSpeckleProject"
          :loading="modelsLoading"
          @click="speckleStore.getSpeckleModels()"
        >
          Refresh models
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      class="d-flex flex-column overflow-y-auto"
      density="comfortable"
      fixed-header
      :items="filteredItems"
      :headers="HEADER"
      :loading="loading"
      color="primary"
    >
      <template #[`item.order`]="{ index }">
        {{ index + 1 }}
      </template>
      <template #[`item.${SPECKLE_MODEL_ID}`]="{ item }">
        <v-select
          :model-value="item[SPECKLE_MODEL_ID] || null"
          :items="allModels"
          item-title="name"
          item-value="id"
          :return-object="false"
          placeholder="Not linked"
          density="compact"
          color="primary"
          class="my-2"
          hide-details="auto"
          clearable
          :loading="isSaving(item) || modelsLoading"
          :disabled="!canEditCalculation || !hasSpeckleProject || isSaving(item)"
          :error-messages="linkErrors(item)"
          @update:model-value="updateLink(item, $event)"
        ></v-select>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="isLinkedToExistingModel(item)"
          icon="mdi-open-in-new"
          size="32"
          variant="text"
          :href="speckleModelUrl(item[SPECKLE_MODEL_ID])"
          target="_blank"
          rel="noopener"
          title="Open model in Speckle"
        ></v-btn>
      </template>
      <template #no-data>
        <NoDataBar text="No calculations in this project yet." />
      </template>
    </v-data-table>

    <BulkActionDialog
      :bulk-action="true"
      action-text="Link by name"
      bulk-action-text="Remove the links of these {amount} calculations and link each one to the Speckle model with the same name? Calculations without a matching model will be left unlinked."
      button-color="primary"
      :items-to-action="items"
      :loading="linkingByName"
      :dialog="linkByNameDialog"
      @toggle-dialog="linkByNameDialog = false"
      @action="linkAllByName"
    ></BulkActionDialog>
  </v-card>
</template>
